/**
 * What the interaction layer has done to her today, per account, on this
 * device.
 *
 * Petting her mark, sassing her back through a chip, getting thrown out of a
 * conversation for being rude — none of it is diary data, none of it syncs,
 * and none of it reaches the push sender. That last part is deliberate: the
 * lock screen computes her mood from the same day series the app does
 * (`voice-facts.ts`), and an input only one side can see would let the two
 * disagree about who she is today. So affection is a device-local overlay, a
 * shade on top of the mood rather than a mood — the same tier as sass memory,
 * stored the same way (`meta`, keyed per account, mirrored in a synchronous
 * cache because `sassLine` callers cannot await).
 *
 * The store is also the ration book. Chips and petting are cheap, frequent
 * triggers — the first features where the user decides how often she speaks —
 * and a pool sized for "however often somebody feels like it" cannot exist.
 * Hard daily caps here are what keep the POOL_META declarations in
 * `catalog/interaction.ts` honest.
 */

import { getMeta, setMeta } from '../db/dexie.js'
import { currentProfile } from '../screens/useProfile.js'
import { awayNow } from './away.js'
import { herToday } from './clock.js'

export type Affection = 'warmed' | 'cooled'

interface InteractionState {
  /** Local date the daily counters below are for. */
  date: string
  petsToday: number
  /** Petting lines already spoken today (the visual always plays). */
  petLinesToday: number
  chipsToday: number
  lastPetAt: number
  /** Local date whose whole remainder she spends cooled after a rude exit. */
  cooledOn: string | null
}

const EMPTY: InteractionState = {
  date: '',
  petsToday: 0,
  petLinesToday: 0,
  chipsToday: 0,
  lastPetAt: 0,
  cooledOn: null,
}

/** Pets in one day after which she counts as warmed. */
const WARMED_AT_PETS = 3
/** Petting lines per day: the first pet speaks, later ones mostly don't. */
const PET_LINES_PER_DAY = 2
/** Strokes closer together than this are one moment of affection, not several. */
const PET_COOLDOWN_MS = 45_000
/** After the first, roughly one pet in four earns a line. */
const PET_LINE_RATE = 1 / 4
/** Toasts offering a reply chip per day. */
const CHIPS_PER_DAY = 3

const ANON_OWNER = 'anon'

function ownerKey(): string {
  return currentProfile()?.id ?? ANON_OWNER
}

function metaKey(owner: string): string {
  return `glitime.affection.${owner}`
}

/** Her day, not the device's — see `clock.ts` for why those can differ. */
function localDate(now: Date): string {
  return herToday(now)
}

const cache = new Map<string, InteractionState>()
const hydrationPromises = new Map<string, Promise<void>>()

function hydrate(owner: string): Promise<void> {
  let promise = hydrationPromises.get(owner)
  if (promise == null) {
    promise = getMeta<InteractionState>(metaKey(owner), EMPTY).then((state) => {
      if (!cache.has(owner)) cache.set(owner, state)
    })
    hydrationPromises.set(owner, promise)
  }
  return promise
}

/** Today's state, counters reset if the stored day is over. */
function read(owner: string, now: Date): InteractionState {
  void hydrate(owner)
  const state = cache.get(owner) ?? EMPTY
  const today = localDate(now)
  if (state.date === today) return state
  // A new day. The cooldown survives only if it was set today (it wasn't —
  // the date changed), so everything resets together.
  return {
    date: today,
    petsToday: 0,
    petLinesToday: 0,
    chipsToday: 0,
    lastPetAt: state.lastPetAt,
    cooledOn: state.cooledOn === today ? state.cooledOn : null,
  }
}

function write(owner: string, state: InteractionState): void {
  cache.set(owner, state)
  void setMeta(metaKey(owner), state)
}

/**
 * The overlay the engine reads on every line. Cooled wins: an apology owed
 * outranks any amount of same-day petting.
 *
 * A closed door counts as cooled too, and that one line is the whole "the
 * app goes cold while she is gone" behaviour: every surface already keys off
 * this — the line register, the chips, the ambient chatter, the wordless
 * petting, the CSS stamp. She is not sulking in the chat sheet only.
 */
export function affectionNow(now: Date = new Date()): Affection | null {
  const state = read(ownerKey(), now)
  if (state.cooledOn === localDate(now) || awayNow() != null) return 'cooled'
  if (state.petsToday >= WARMED_AT_PETS) return 'warmed'
  return null
}

/**
 * A pet landed. Returns whether this one gets a spoken line on top of the
 * animation — the first of the day does, later ones rarely, a cooled day
 * never (she is not accepting affection as currency today).
 */
export function recordPet(now: Date = new Date(), rng: () => number = Math.random): boolean {
  const owner = ownerKey()
  const state = read(owner, now)
  const cooled = state.cooledOn === localDate(now) || awayNow() != null

  // A flurry of strokes in one go is one moment of affection, not three.
  // `lastPetAt` was written and never read, so "warmed after three pets"
  // could be earned in two seconds; the animation still plays either way.
  const inFlurry = state.lastPetAt != null && now.getTime() - state.lastPetAt < PET_COOLDOWN_MS
  const speaks =
    !cooled &&
    !inFlurry &&
    state.petLinesToday < PET_LINES_PER_DAY &&
    (state.petsToday === 0 || rng() < PET_LINE_RATE)
  write(owner, {
    ...state,
    petsToday: inFlurry ? state.petsToday : state.petsToday + 1,
    petLinesToday: speaks ? state.petLinesToday + 1 : state.petLinesToday,
    lastPetAt: now.getTime(),
  })
  return speaks
}

/** A rude chat exit: cooled for the rest of the local day. */
export function recordRudeExit(now: Date = new Date()): void {
  const owner = ownerKey()
  const state = read(owner, now)
  write(owner, { ...state, cooledOn: localDate(now) })
  stampCooled(true)
}

/**
 * The apology landed. She thaws immediately rather than serving out the day —
 * a cooldown that cannot be repaired is a punishment, and she is not that.
 * The pet counters are left where they are: an apology is not affection, it
 * is the day going back to normal.
 */
export function clearCooled(now: Date = new Date()): void {
  const owner = ownerKey()
  const state = read(owner, now)
  write(owner, { ...state, cooledOn: null })
  stampCooled(false)
}

/** Whether a persona toast may carry reply chips right now, and note it if so. */
export function takeChipSlot(now: Date = new Date()): boolean {
  const owner = ownerKey()
  const state = read(owner, now)
  if (state.chipsToday >= CHIPS_PER_DAY) return false
  write(owner, { ...state, chipsToday: state.chipsToday + 1 })
  return true
}

/**
 * The cooled shade, made visible: one attribute, read by `persona.css`. The
 * presence controller calls this on boot and day change so a reload keeps the
 * shade; a rude exit stamps it immediately.
 */
export function stampCooled(cooled: boolean): void {
  if (typeof document === 'undefined') return
  if (cooled) {
    document.documentElement.dataset.herCooled = '1'
  } else {
    delete document.documentElement.dataset.herCooled
  }
}

/** Re-assert the attribute from stored state — for boot and midnight rollover. */
export function syncCooledStamp(now: Date = new Date()): void {
  stampCooled(affectionNow(now) === 'cooled')
}

/** Test-only: drop the cache so the next read starts cold. */
export function __resetInteractionForTests(): void {
  cache.clear()
  hydrationPromises.clear()
}

/** Test-only: wait for an account's state to finish loading. */
export function __hydrateInteractionForTests(owner: string): Promise<void> {
  return hydrate(owner)
}
